import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTranslations } from '@/i18n/use-locale';
import { useLocalizedName } from '@/i18n/localized-name';
import { TalentsTable, type TalentsFilter } from '@/components/admin/talents-table';
import type { TalentSearchHit } from '@/contracts';
import type { ListReturnState } from '@/lib/detail';
import { DeleteTalentPanel, RemovePhotoPanel } from './talent-action-panels';
import { QueryGate } from '@/components/ui/query-gate';
import { useSpecialtiesQuery, useTalentsQuery } from '@/lib/queries';
import { PageHeader } from '@/components/admin/page-header';

/** The status chip lives in `?status=`, so the detail screen's back link can restore it. */
function parseFilter(value: string | null): TalentsFilter {
  return value === 'verified' || value === 'pending' ? value : 'all';
}

export function TalentsRoute() {
  const t = useTranslations('admin.talents');
  const name = useLocalizedName();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = parseFilter(searchParams.get('status'));

  const [deleting, setDeleting] = useState<TalentSearchHit | null>(null);
  // The photo removal asks for a reason the same way the delete does: the talent is
  // told why, so "no reason" is not an option the form offers.
  const [removingPhoto, setRemovingPhoto] = useState<TalentSearchHit | null>(null);

  const talentsQuery = useTalentsQuery();

  // Reference data (ADR-004): the id→name catalogue the mobile app and the public
  // profile read too. Held for the session; the ids on each row are resolved against it.
  const specialtiesQuery = useSpecialtiesQuery();

  const specialtyNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of specialtiesQuery.data ?? []) {
      map[s.id] = name(s);
    }
    return map;
  }, [specialtiesQuery.data, name]);

  const returnState: ListReturnState = { from: `${location.pathname}${location.search}` };
  const detailHref = useCallback((row: TalentSearchHit) => `/talents/${row.id}`, []);

  return (
    <QueryGate query={talentsQuery}>
      {(data) => {
        const items = data.items;

        return (
          <div className="space-y-6">
            <PageHeader title={t('screenTitle')} sub={t('screenHint')}>
              {/*
                The badge is not granted here: it follows the document decisions (API
                migration 0027), so the admin reading «قيد التوثيق» knows where to go.
              */}
              <p className="text-[12px] text-conc-400">{t('badgeNote')}</p>
            </PageHeader>

            {deleting && (
              <DeleteTalentPanel
                key={deleting.id}
                talent={deleting}
                onCancel={() => setDeleting(null)}
                onDeleted={() => setDeleting(null)}
              />
            )}

            {removingPhoto && (
              <RemovePhotoPanel
                key={removingPhoto.id}
                talent={removingPhoto}
                onCancel={() => setRemovingPhoto(null)}
                onRemoved={() => setRemovingPhoto(null)}
              />
            )}

            {/* The route sits behind `candidates.manage` (nav.ts), which is what gates both
          the delete and the photo removal — an admin without it never reaches here. */}
            <TalentsTable
              rows={items}
              specialtyNames={specialtyNames}
              onDelete={setDeleting}
              onRemovePhoto={setRemovingPhoto}
              detailHref={detailHref}
              detailState={returnState}
              onOpen={(row) => void navigate(detailHref(row), { state: returnState })}
              filter={filter}
              onFilterChange={(next) =>
                setSearchParams(
                  (current) => {
                    const params = new URLSearchParams(current);
                    if (next === 'all') params.delete('status');
                    else params.set('status', next);
                    return params;
                  },
                  { replace: true },
                )
              }
            />
          </div>
        );
      }}
    </QueryGate>
  );
}
